import { useState, useEffect } from 'react'
import { supabase } from '../../api/supabase'
import SearchableSelect from '../SearchableSelect'
import PortLocodeWarning from '../PortLocodeWarning'
import ContainersTab from './ContainersTab'

interface ShipDetailsTabProps {
  jobData: any
  setJobData: (data: any) => void
  readOnly?: boolean
}

interface Port {
  id: string
  code: string
  name: string
  type: string
  un_locode?: string | null
}

interface Carrier {
  id: string
  code: string
  name: string
  type: string
  scac_code?: string | null
  iata_prefix?: string | null
}

// The ports table has grown to 40k+ rows (a bulk UN/LOCODE import sits alongside the curated
// seaports/airports) — far past PostgREST's default page size, so loading the whole table
// client-side silently truncates it and common ports (e.g. NGB, FXT) never make it into the
// list to search through. Search the table server-side instead, matching code/name/un_locode.
function escapeIlikeTerm(term: string): string {
  // strip characters with special meaning in PostgREST's or()/ilike syntax rather than
  // trying to escape them — port codes/names never legitimately contain these.
  return term.trim().replace(/[%_,()]/g, '')
}

// Z33: each port type has exactly one authoritative identifier now, not two shown together --
// SEAPORT's is the UN/LOCODE (5-char, e.g. GBFXT; the 3-letter `code` is an internal/BoxTop
// field, not meant to disambiguate in the UI), AIRPORT's is the IATA code (3-char, e.g. LHR --
// LOCODE is optional/unused for airports). Showing both, or the wrong one, reads as ambiguous
// now that Z27/Z29 made `code`/`un_locode` unique only per type, not globally (the same 3-4
// letters can legitimately name both a seaport and an airport in different cities).
function formatPortLabel(p: { code: string | null; name: string; type?: string | null; un_locode?: string | null }): string {
  const identifier = p.type === 'SEAPORT' ? p.un_locode : p.code
  const warn = !identifier ? ' ▲' : ''
  return `${identifier || '—'} - ${p.name}${warn}`
}

export default function ShipDetailsTab({ jobData, setJobData, readOnly = false }: ShipDetailsTabProps) {
  const [carriers, setCarriers] = useState<Carrier[]>([])
  const [shippingTerms, setShippingTerms] = useState<{ id: string; code: string; description: string }[]>([])
  const [loading, setLoading] = useState(true)
  // Ports actually referenced by this job, keyed by id — resolved by direct id lookup (not
  // an is_active filter) so a port that's since been deactivated still displays correctly
  // instead of appearing to have been silently cleared.
  const [pinnedPorts, setPinnedPorts] = useState<Record<string, Port>>({})
  // Same reasoning as pinnedPorts: if air_details.airline_id points at a carrier that's since
  // been deactivated, resolve it directly by id so the field still shows its name instead of
  // rendering blank.
  const [pinnedAirlines, setPinnedAirlines] = useState<Record<string, Carrier>>({})

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [carriersRes, termsRes] = await Promise.all([
        supabase.from('carriers').select('id, code, name, type, scac_code, iata_prefix').eq('is_active', true).order('name'),
        supabase.from('shipping_terms').select('id, code, description').eq('is_active', true).order('code'),
      ])

      if (carriersRes.error) throw carriersRes.error
      if (termsRes.error) throw termsRes.error

      setCarriers(carriersRes.data || [])
      setShippingTerms(termsRes.data || [])
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  const selectedPortIds = [
    jobData.sea_details?.departure_port_id,
    jobData.sea_details?.arrival_port_id,
    jobData.air_details?.departure_airport_id,
    jobData.air_details?.arrival_airport_id,
  ].filter(Boolean) as string[]
  const selectedPortIdsKey = selectedPortIds.slice().sort().join(',')

  useEffect(() => {
    const missing = [...new Set(selectedPortIds)].filter(id => !pinnedPorts[id])
    if (missing.length === 0) return
    supabase
      .from('ports')
      .select('id, code, name, type, un_locode')
      .in('id', missing)
      .then(({ data, error }) => {
        if (error) {
          console.error('[ShipDetailsTab] Error resolving selected ports:', error)
          return
        }
        if (data && data.length > 0) {
          setPinnedPorts(prev => {
            const next = { ...prev }
            data.forEach(p => { next[p.id] = p })
            return next
          })
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPortIdsKey])

  const getPinnedOption = (id: string | undefined) => {
    if (!id) return null
    const p = pinnedPorts[id]
    return p ? { id: p.id, label: formatPortLabel(p), code: p.code, name: p.name } : null
  }

  const handlePortUpdated = (updated: { id?: string; code: string | null; name: string; type?: string | null; un_locode?: string | null }) => {
    if (!updated.id) return
    const port: Port = { id: updated.id, code: updated.code || '', name: updated.name, type: updated.type || '', un_locode: updated.un_locode }
    setPinnedPorts(prev => ({ ...prev, [port.id]: port }))
  }

  const airlineId = jobData.air_details?.airline_id as string | undefined

  useEffect(() => {
    if (!airlineId || carriers.some(c => c.id === airlineId) || pinnedAirlines[airlineId]) return
    supabase
      .from('carriers')
      .select('id, code, name, type, scac_code, iata_prefix')
      .eq('id', airlineId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) {
          console.error('[ShipDetailsTab] Error resolving selected airline:', error)
          return
        }
        if (data) setPinnedAirlines(prev => ({ ...prev, [data.id]: data }))
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [airlineId])

  const getPinnedAirlineOption = (id: string | undefined) => {
    if (!id) return null
    const c = pinnedAirlines[id]
    return c ? { id: c.id, label: `${c.code} - ${c.name}`, code: c.code, name: c.name, iata_prefix: c.iata_prefix } : null
  }

  // Z14 bug 1: MAWB numbers carry a 3-digit IATA airline prefix (e.g. "618-56609571" -> 618 ->
  // TransGlobal Air) — auto-fill the Airline field from it as the user types the MAWB.
  const handleMawbChange = (value: string) => {
    const nextAirDetails = { ...(jobData.air_details || {}), mawb_no: value }
    const prefix = value.split('-')[0]?.trim()
    if (prefix && /^\d{3}$/.test(prefix)) {
      const match = carriers.find(c => c.type === 'AIRLINE' && c.iata_prefix === prefix)
      if (match) nextAirDetails.airline_id = match.id
    }
    setJobData({ ...jobData, air_details: nextAirDetails })
  }

  // Z20: code/UN-LOCODE match by PREFIX (typing "FXT" or "NGB" finds that port; typing
  // "XT" alone doesn't), name matches by SUBSTRING (typing "FELIXSTOWE" still finds it).
  // ilike is already case-insensitive. ports.aliases[] doesn't exist in the schema yet --
  // there's nowhere to add it from here (no migration in this change) -- so alias matching
  // isn't wired in; add `aliases.cs.{${cleaned}}`-style matching here once that column exists.
  const searchPorts = async (term: string, portType: 'SEAPORT' | 'AIRPORT') => {
    const cleaned = escapeIlikeTerm(term)
    let query = supabase
      .from('ports')
      .select('id, code, name, type, un_locode')
      .eq('is_active', true)
      .eq('type', portType)
      .order('code')
      .limit(50)
    if (cleaned) {
      query = query.or(`name.ilike.%${cleaned}%,code.ilike.${cleaned}%,un_locode.ilike.${cleaned}%`)
    }
    const { data, error } = await query
    if (error) {
      console.error('[searchPorts] Error:', error)
      return []
    }
    return (data || []).map(p => ({ id: p.id, label: formatPortLabel(p), code: p.code, name: p.name }))
  }

  const createPort = async (name: string, portType: 'SEAPORT' | 'AIRPORT' = 'SEAPORT') => {
    try {
      console.log('[createPort] Creating port:', { name, portType })

      // Generate code: first 5 chars uppercase, remove special chars
      let code = name
        .substring(0, 5)
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')

      // Ensure code is not empty
      if (!code) {
        code = name.replace(/[^A-Z0-9]/g, '').substring(0, 5).toUpperCase()
      }
      if (!code) {
        throw new Error('Cannot generate valid code from port name')
      }

      console.log('[createPort] Generated code:', code)

      // Map UI type to database type (SEAPORT or AIRPORT)
      const dbType = portType === 'SEAPORT' ? 'SEAPORT' : 'AIRPORT'

      // K5: BoxTop needs a real UN/LOCODE (e.g. 'GBFXT'), not the auto-generated `code` above --
      // there's no create/edit modal for ports (just this inline "create from search" flow), so
      // ask for it here. Optional/skippable: sync falls back to the plain `code` if left blank.
      const unLocodeInput = window.prompt(
        `UN/LOCODE for "${name.trim()}" (5-char, e.g. GBFXT) — optional, needed for BoxTop sync. Leave blank to skip.`
      )
      const un_locode = unLocodeInput?.trim().toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 5) || null

      // Try insert with generated code
      const insertData = {
        code,
        name: name.trim(),
        type: dbType,
        country: null,
        un_locode,
        is_active: true,
      }

      console.log('[createPort] Inserting:', insertData)

      let insertResult = await supabase
        .from('ports')
        .insert([insertData])
        .select('id, code, name, type, un_locode')
        .single()

      // If code conflict, retry with random suffix
      if (insertResult.error) {
        const errorMsg = (insertResult.error as any).message || ''
        const isUniqueViolation = errorMsg.includes('unique') || errorMsg.includes('duplicate')

        if (isUniqueViolation) {
          console.log('[createPort] Code conflict, retrying with random suffix...')
          const randomSuffix = Math.floor(Math.random() * 100)
            .toString()
            .padStart(2, '0')
          const newCode = `${code.substring(0, 3)}${randomSuffix}`
          console.log('[createPort] Retrying with code:', newCode)

          insertResult = await supabase
            .from('ports')
            .insert([{ ...insertData, code: newCode }])
            .select('id, code, name, type, un_locode')
            .single()
        }
      }

      if (insertResult.error) {
        console.error('[createPort] Supabase error:', insertResult.error)
        const errorMsg = (insertResult.error as any).message || (insertResult.error as any).details || 'Unknown error'
        const errorCode = (insertResult.error as any).code || ''
        throw new Error(`${errorMsg}${errorCode ? ` (${errorCode})` : ''}`)
      }

      const data = insertResult.data
      console.log('[createPort] Port created successfully:', data)
      setPinnedPorts(prev => ({ ...prev, [data.id]: data }))
      return { id: data.id, label: formatPortLabel(data) }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'
      console.error('[createPort] Exception:', errorMsg, error)
      throw new Error(`Failed to create port: ${errorMsg}`)
    }
  }

  const createCarrier = async (name: string, carrierType: 'SHIPPING_LINE' | 'AIRLINE' = 'SHIPPING_LINE') => {
    try {
      console.log('[createCarrier] Creating carrier:', { name, carrierType })

      // Generate code: first 3 chars uppercase
      let code = name.substring(0, 3).toUpperCase().replace(/[^A-Z0-9]/g, '')

      // Ensure code is not empty
      if (!code) {
        code = name.replace(/[^A-Z0-9]/g, '').substring(0, 3).toUpperCase()
      }
      if (!code) {
        throw new Error('Cannot generate valid code from carrier name')
      }

      console.log('[createCarrier] Generated code:', code)

      // K5: BoxTop needs a real 4-letter SCAC code (e.g. 'COSU' for COSCO), not the auto-generated
      // `code` above -- there's no create/edit modal for carriers (just this inline "create from
      // search" flow), so ask for it here. Optional/skippable: sync omits CarrierCode if blank
      // rather than sending a guessed value.
      const scacInput = window.prompt(
        `SCAC code for "${name.trim()}" (4-letter, e.g. COSU) — optional, needed for BoxTop sync. Leave blank to skip.`
      )
      const scac_code = scacInput?.trim().toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 4) || null

      const insertData = {
        code,
        name: name.trim(),
        type: carrierType,
        scac_code,
        is_active: true,
      }

      console.log('[createCarrier] Inserting:', insertData)

      let insertResult = await supabase
        .from('carriers')
        .insert([insertData])
        .select('id, code, name, type, scac_code')
        .single()

      // If code conflict, retry with random suffix
      if (insertResult.error) {
        const errorMsg = (insertResult.error as any).message || ''
        const isUniqueViolation = errorMsg.includes('unique') || errorMsg.includes('duplicate')

        if (isUniqueViolation) {
          console.log('[createCarrier] Code conflict, retrying with random suffix...')
          const randomSuffix = Math.floor(Math.random() * 100)
            .toString()
            .padStart(2, '0')
          const newCode = `${code.substring(0, 2)}${randomSuffix}`
          console.log('[createCarrier] Retrying with code:', newCode)

          insertResult = await supabase
            .from('carriers')
            .insert([{ ...insertData, code: newCode }])
            .select('id, code, name, type, scac_code')
            .single()
        }
      }

      if (insertResult.error) {
        console.error('[createCarrier] Supabase error:', insertResult.error)
        const errorMsg = (insertResult.error as any).message || (insertResult.error as any).details || 'Unknown error'
        const errorCode = (insertResult.error as any).code || ''
        throw new Error(`${errorMsg}${errorCode ? ` (${errorCode})` : ''}`)
      }

      const data = insertResult.data
      console.log('[createCarrier] Carrier created successfully:', data)
      setCarriers([...carriers, data])
      return { id: data.id, label: `${data.code} - ${data.name}` }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'
      console.error('[createCarrier] Exception:', errorMsg, error)
      throw new Error(`Failed to create carrier: ${errorMsg}`)
    }
  }

  const updateField = (field: string, value: any) => {
    setJobData({ ...jobData, [field]: value })
  }

  const updateNestedField = (table: string, field: string, value: any) => {
    setJobData({
      ...jobData,
      [table]: {
        ...(jobData[table] || {}),
        [field]: value
      }
    })
  }

  if (loading) return <div className="text-center py-8">Loading data...</div>

  return (
    <div className="space-y-6">
      {/* Common Fields */}
      <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg space-y-4">
        <h3 className="font-medium text-gray-900 dark:text-white">Basic Information</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Booking Reference
            </label>
            <input
              type="text"
              value={jobData.booking_ref || ''}
              onChange={(e) => updateField('booking_ref', e.target.value)}
              disabled={readOnly}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
              placeholder="e.g., BK123456"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Client Reference
            </label>
            <input
              type="text"
              value={jobData.client_ref || ''}
              onChange={(e) => updateField('client_ref', e.target.value)}
              disabled={readOnly}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
              placeholder="e.g., CLT-001"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Booked Date
            </label>
            <input
              type="date"
              value={jobData.booked_date || new Date().toISOString().split('T')[0]}
              onChange={(e) => updateField('booked_date', e.target.value)}
              disabled={readOnly}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status
            </label>
            <SearchableSelect
              options={[
                { id: 'PRE', label: 'Pre-Alert' },
                { id: 'CUS', label: 'Custom' },
                { id: 'CTR', label: 'Container' },
                { id: 'DEL', label: 'Delivered' },
                { id: 'PDY', label: 'Pending Delivery' },
                { id: 'POD', label: 'Proof of Delivery' },
                { id: 'INV', label: 'Invoiced' },
                { id: 'WAR', label: 'Warehouse' },
                { id: 'RET', label: 'Return' },
                { id: 'COM', label: 'Completed' },
                { id: 'PRO', label: 'Processing' },
                { id: 'CAN', label: 'Cancelled' },
                { id: 'DEF', label: 'Default' },
                { id: 'WKD', label: 'Worked' },
                { id: 'EXP', label: 'Expired' },
                { id: 'SDI', label: 'Standby Delivery Item' },
                { id: 'REQ', label: 'Requested' }
              ]}
              value={jobData.status || 'PRE'}
              onChange={(value) => updateField('status', value)}
              disabled={readOnly}
              placeholder="Select status..."
            />
          </div>
        </div>
      </div>

      {/* Air Details */}
      {jobData.transport_mode === 'AIR' && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-900 p-4 rounded-lg space-y-4">
          <h3 className="font-medium text-gray-900 dark:text-white">Air Transportation</h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                MAWB Number
              </label>
              <input
                type="text"
                value={jobData.air_details?.mawb_no || ''}
                onChange={(e) => handleMawbChange(e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
                placeholder="Master Bill of Lading"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                HAWB Number
              </label>
              <input
                type="text"
                value={jobData.air_details?.hawb_no || ''}
                onChange={(e) => updateNestedField('air_details', 'hawb_no', e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
                placeholder="House Bill of Lading"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Airline <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                options={carriers.filter(c => c.type === 'AIRLINE').map(c => ({
                  id: c.id,
                  label: `${c.code} - ${c.name}`,
                  code: c.code, name: c.name, iata_prefix: c.iata_prefix
                }))}
                codeKey="code" nameKey="name" extraCodeKeys={['iata_prefix']}
                selectedOption={getPinnedAirlineOption(jobData.air_details?.airline_id)}
                value={jobData.air_details?.airline_id || ''}
                onChange={(value) => updateNestedField('air_details', 'airline_id', value)}
                disabled={readOnly}
                placeholder="Search airline (code, name, or IATA prefix e.g. 618)..."
                onCreate={(name) => createCarrier(name, 'AIRLINE')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Flight Number
              </label>
              <input
                type="text"
                value={jobData.air_details?.flight_no || ''}
                onChange={(e) => updateNestedField('air_details', 'flight_no', e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
                placeholder="e.g., BA123"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Aircraft Reg.
              </label>
              <input
                type="text"
                value={jobData.air_details?.aircraft_reg || ''}
                onChange={(e) => updateNestedField('air_details', 'aircraft_reg', e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
                placeholder="e.g., G-ABCD"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Departure Airport
                <PortLocodeWarning port={pinnedPorts[jobData.air_details?.departure_airport_id]} onUpdated={handlePortUpdated} />
              </label>
              <SearchableSelect
                options={[]}
                onSearch={(term) => searchPorts(term, 'AIRPORT')}
                selectedOption={getPinnedOption(jobData.air_details?.departure_airport_id)}
                value={jobData.air_details?.departure_airport_id || ''}
                onChange={(value) => updateNestedField('air_details', 'departure_airport_id', value)}
                disabled={readOnly}
                placeholder="Search departure airport (code or name)..."
                onCreate={(name) => createPort(name, 'AIRPORT')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Arrival Airport
                <PortLocodeWarning port={pinnedPorts[jobData.air_details?.arrival_airport_id]} onUpdated={handlePortUpdated} />
              </label>
              <SearchableSelect
                options={[]}
                onSearch={(term) => searchPorts(term, 'AIRPORT')}
                selectedOption={getPinnedOption(jobData.air_details?.arrival_airport_id)}
                value={jobData.air_details?.arrival_airport_id || ''}
                onChange={(value) => updateNestedField('air_details', 'arrival_airport_id', value)}
                disabled={readOnly}
                placeholder="Search arrival airport (code or name)..."
                onCreate={(name) => createPort(name, 'AIRPORT')}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Departure Date
              </label>
              <input
                type="date"
                value={jobData.air_details?.departure_date || ''}
                onChange={(e) => updateNestedField('air_details', 'departure_date', e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Arrival Date
              </label>
              <input
                type="date"
                value={jobData.air_details?.arrival_date || ''}
                onChange={(e) => updateNestedField('air_details', 'arrival_date', e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Customs Cleared Date
              </label>
              <input
                type="date"
                value={jobData.air_details?.customs_cleared_date || ''}
                onChange={(e) => updateNestedField('air_details', 'customs_cleared_date', e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
              />
            </div>
            <div></div>
          </div>
        </div>
      )}

      {/* Sea Details */}
      {jobData.transport_mode === 'SEA' && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900 p-4 rounded-lg space-y-4">
          <h3 className="font-medium text-gray-900 dark:text-white">Sea Transportation</h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Load Type
              </label>
              <SearchableSelect
                options={[
                  { id: 'FCL', label: 'FCL (Full Container Load)' },
                  { id: 'LCL', label: 'LCL (Less than Container Load)' },
                  { id: 'GROUPAGE', label: 'Groupage' }
                ]}
                value={jobData.sea_details?.load_type || 'LCL'}
                onChange={(value) => updateNestedField('sea_details', 'load_type', value)}
                disabled={readOnly}
                placeholder="Select load type..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Shipping Line
              </label>
              <SearchableSelect
                options={carriers.filter(c => c.type === 'SHIPPING_LINE').map(c => ({
                  id: c.id,
                  label: `${c.code} - ${c.name}`,
                  code: c.code, name: c.name, scac_code: c.scac_code
                }))}
                codeKey="code" nameKey="name" extraCodeKeys={['scac_code']}
                value={jobData.sea_details?.shipping_line_id || ''}
                onChange={(value) => updateNestedField('sea_details', 'shipping_line_id', value)}
                disabled={readOnly}
                placeholder="Search shipping line (code, name, or SCAC)..."
                onCreate={(name) => createCarrier(name, 'SHIPPING_LINE')}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Vessel Name
              </label>
              <input
                type="text"
                value={jobData.sea_details?.vessel_name || ''}
                onChange={(e) => updateNestedField('sea_details', 'vessel_name', e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
                placeholder="e.g., Ever Given"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Voyage Number
              </label>
              <input
                type="text"
                value={jobData.sea_details?.voyage_no || ''}
                onChange={(e) => updateNestedField('sea_details', 'voyage_no', e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
                placeholder="e.g., VOY123"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Master B/L Number
              </label>
              <input
                type="text"
                value={jobData.sea_details?.master_bl_no || ''}
                onChange={(e) => updateNestedField('sea_details', 'master_bl_no', e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                House B/L Number
              </label>
              <input
                type="text"
                value={jobData.sea_details?.house_bl_no || ''}
                onChange={(e) => updateNestedField('sea_details', 'house_bl_no', e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Freight Terms
              </label>
              <SearchableSelect
                options={shippingTerms.map(t => ({ id: t.id, label: `${t.code} - ${t.description}` }))}
                value={jobData.sea_details?.shipping_terms_id || ''}
                onChange={(value) => updateNestedField('sea_details', 'shipping_terms_id', value)}
                disabled={readOnly}
                placeholder="Search freight terms..."
                allowCreate={false}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Freight Type
              </label>
              <SearchableSelect
                options={[
                  { id: 'prepaid', label: 'Prepaid' },
                  { id: 'collect', label: 'Collect' },
                ]}
                value={jobData.sea_details?.freight_type || ''}
                onChange={(value) => updateNestedField('sea_details', 'freight_type', value)}
                disabled={readOnly}
                placeholder="Select freight type..."
                allowCreate={false}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Place of Loading
              </label>
              <input
                type="text"
                value={jobData.sea_details?.place_of_loading || ''}
                onChange={(e) => updateNestedField('sea_details', 'place_of_loading', e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
                placeholder="e.g., Shanghai"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Final Destination
              </label>
              <input
                type="text"
                value={jobData.sea_details?.final_destination || ''}
                onChange={(e) => updateNestedField('sea_details', 'final_destination', e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
                placeholder="e.g., Birmingham"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Departure Port
                <PortLocodeWarning port={pinnedPorts[jobData.sea_details?.departure_port_id]} onUpdated={handlePortUpdated} />
              </label>
              <SearchableSelect
                options={[]}
                onSearch={(term) => searchPorts(term, 'SEAPORT')}
                selectedOption={getPinnedOption(jobData.sea_details?.departure_port_id)}
                value={jobData.sea_details?.departure_port_id || ''}
                onChange={(value) => updateNestedField('sea_details', 'departure_port_id', value)}
                disabled={readOnly}
                placeholder="Search departure port (code or name)..."
                onCreate={(name) => createPort(name, 'SEAPORT')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Arrival Port
                <PortLocodeWarning port={pinnedPorts[jobData.sea_details?.arrival_port_id]} onUpdated={handlePortUpdated} />
              </label>
              <SearchableSelect
                options={[]}
                onSearch={(term) => searchPorts(term, 'SEAPORT')}
                selectedOption={getPinnedOption(jobData.sea_details?.arrival_port_id)}
                value={jobData.sea_details?.arrival_port_id || ''}
                onChange={(value) => updateNestedField('sea_details', 'arrival_port_id', value)}
                disabled={readOnly}
                placeholder="Search arrival port (code or name)..."
                onCreate={(name) => createPort(name, 'SEAPORT')}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Departure Date
              </label>
              <input
                type="date"
                value={jobData.sea_details?.departure_date || ''}
                onChange={(e) => updateNestedField('sea_details', 'departure_date', e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Arrival Date
              </label>
              <input
                type="date"
                value={jobData.sea_details?.arrival_date || ''}
                onChange={(e) => updateNestedField('sea_details', 'arrival_date', e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100"
              />
            </div>
          </div>
        </div>
      )}

      <ContainersTab jobData={jobData} setJobData={setJobData} readOnly={readOnly} />
    </div>
  )
}
